import { useState, useRef, useCallback, useEffect } from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import { AlignLeft, AlignCenter, AlignRight, Maximize, Crop, RotateCw, Trash2 } from 'lucide-react';
import ImageCropModal from './ImageCropModal';

const ResizableImageComponent = ({ node, updateAttributes, selected, deleteNode, editor }) => {
  const { src, alt, width, height, align } = node.attrs;
  const [resizing, setResizing] = useState(false);
  const [showCrop, setShowCrop] = useState(false);
  const imgRef = useRef(null);
  const startRef = useRef({ x: 0, y: 0, w: 0, h: 0 });

  const wrapperStyle = {
    left: { float: 'left', margin: '0 1.5rem 1rem 0', clear: 'left' },
    right: { float: 'right', margin: '0 0 1rem 1.5rem', clear: 'right' },
    center: { display: 'flex', justifyContent: 'center', margin: '1.5rem 0', clear: 'both' },
    full: { display: 'block', margin: '1.5rem 0', clear: 'both' },
  }[align] || {};

  const imgStyle = {
    width: width || (align === 'full' ? '100%' : 'auto'),
    height: height || 'auto',
    maxWidth: '100%',
    cursor: 'pointer',
    borderRadius: '4px',
  };

  const onMouseDown = useCallback((e, corner) => {
    e.preventDefault();
    e.stopPropagation();
    const img = imgRef.current;
    if (!img) return;
    startRef.current = { x: e.clientX, y: e.clientY, w: img.offsetWidth, h: img.offsetHeight, corner };
    setResizing(true);

    const onMouseMove = (ev) => {
      const { x, y, w, h, corner: c } = startRef.current;
      let dx = ev.clientX - x;
      let newW = c.includes('e') ? w + dx : c.includes('w') ? w - dx : w;
      const ratio = h / w;
      let newH = Math.round(newW * ratio);
      newW = Math.max(80, Math.round(newW));
      newH = Math.max(40, newH);
      updateAttributes({ width: `${newW}px`, height: `${newH}px` });
    };

    const onMouseUp = () => {
      setResizing(false);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [updateAttributes]);

  const handleCropComplete = (newSrc) => {
    updateAttributes({ src: newSrc });
    setShowCrop(false);
  };

  const handleRotate = async () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new window.Image();
    img.crossOrigin = 'anonymous';

    img.onload = async () => {
      canvas.width = img.height;
      canvas.height = img.width;
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate(Math.PI / 2);
      ctx.drawImage(img, -img.width / 2, -img.height / 2);

      canvas.toBlob(async (blob) => {
        if (!blob) return;
        const formData = new FormData();
        formData.append('file', blob, 'rotated-image.jpg');
        const token = localStorage.getItem('token');
        const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
        try {
          const res = await fetch(`${API}/dropbox/upload`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            body: formData,
          });
          const data = await res.json();
          if (data.url) {
            updateAttributes({ src: data.url, width: `${canvas.width}px`, height: `${canvas.height}px` });
          }
        } catch (err) {
          console.error('Rotate upload failed:', err);
        }
      }, 'image/jpeg', 0.9);
    };
    img.src = src;
  };

  return (
    <NodeViewWrapper
      style={wrapperStyle}
      data-align={align}
      className={`relative group ${selected ? 'ring-2 ring-blue-400 ring-offset-2 rounded' : ''}`}
    >
      <div className="relative inline-block" style={{ width: width || 'auto' }}>
        <img
          ref={imgRef}
          src={src}
          alt={alt || ''}
          style={imgStyle}
          draggable={false}
          data-testid="editor-image"
        />

        {selected && (
          <>
            {/* Alignment & tools toolbar */}
            <div
              className="absolute -top-12 left-1/2 -translate-x-1/2 flex items-center gap-1 bg-stone-900 text-white rounded-lg px-2 py-1.5 shadow-xl z-50"
              contentEditable={false}
            >
              <button
                onClick={() => updateAttributes({ align: 'left' })}
                className={`p-1.5 rounded hover:bg-stone-700 ${align === 'left' ? 'bg-stone-700' : ''}`}
                title="Float left"
                data-testid="img-align-left"
              >
                <AlignLeft size={15} />
              </button>
              <button
                onClick={() => updateAttributes({ align: 'center' })}
                className={`p-1.5 rounded hover:bg-stone-700 ${align === 'center' ? 'bg-stone-700' : ''}`}
                title="Center"
                data-testid="img-align-center"
              >
                <AlignCenter size={15} />
              </button>
              <button
                onClick={() => updateAttributes({ align: 'right' })}
                className={`p-1.5 rounded hover:bg-stone-700 ${align === 'right' ? 'bg-stone-700' : ''}`}
                title="Float right"
                data-testid="img-align-right"
              >
                <AlignRight size={15} />
              </button>
              <button
                onClick={() => updateAttributes({ align: 'full' })}
                className={`p-1.5 rounded hover:bg-stone-700 ${align === 'full' ? 'bg-stone-700' : ''}`}
                title="Full width"
                data-testid="img-align-full"
              >
                <Maximize size={15} />
              </button>
              <div className="w-px h-5 bg-stone-600 mx-1" />
              <button
                onClick={() => setShowCrop(true)}
                className="p-1.5 rounded hover:bg-stone-700"
                title="Crop"
                data-testid="img-crop-btn"
              >
                <Crop size={15} />
              </button>
              <button
                onClick={handleRotate}
                className="p-1.5 rounded hover:bg-stone-700"
                title="Rotate 90°"
                data-testid="img-rotate-btn"
              >
                <RotateCw size={15} />
              </button>
              <div className="w-px h-5 bg-stone-600 mx-1" />
              <button
                onClick={deleteNode}
                className="p-1.5 rounded hover:bg-red-700"
                title="Delete image"
                data-testid="img-delete-btn"
              >
                <Trash2 size={15} />
              </button>
            </div>

            {/* Resize handles */}
            {['nw', 'ne', 'sw', 'se'].map((corner) => (
              <div
                key={corner}
                onMouseDown={(e) => onMouseDown(e, corner)}
                className="absolute w-3 h-3 bg-blue-500 border-2 border-white rounded-full shadow cursor-nwse-resize z-40"
                style={{
                  top: corner.includes('n') ? -6 : 'auto',
                  bottom: corner.includes('s') ? -6 : 'auto',
                  left: corner.includes('w') ? -6 : 'auto',
                  right: corner.includes('e') ? -6 : 'auto',
                  cursor: corner === 'ne' || corner === 'sw' ? 'nesw-resize' : 'nwse-resize',
                }}
              />
            ))}
          </>
        )}
      </div>

      {showCrop && (
        <ImageCropModal
          src={src}
          onComplete={handleCropComplete}
          onClose={() => setShowCrop(false)}
        />
      )}
    </NodeViewWrapper>
  );
};

export default ResizableImageComponent;
